import { useBasket } from '../../../bff-api/baskets/useBasket';
import { useProducts } from '../../../bff-api/products/useProducts';
import { isBasketEligibleForMonthlyLoan } from '../../../utils/afterPay/isBasketEligibleForMonthlyLoan';
import { useCustomPreferences } from '../../../utils/api';
import { CustomPreference } from '../../../utils/api/CustomPreference';
import styles from '../afterpay.module.scss';
import type { PaymentMethodConfig } from '../../../bff-api/baskets/model';
import type { Product } from '../../../bff-api/products/model';

interface Props {
    eligible: boolean;
    afterpayConfig: PaymentMethodConfig;
    product?: Product;
}

export const AfterPayMessaging: React.FC<Props> = ({
    eligible,
    afterpayConfig,
    product,
}: Props) => {
    const { data: basket } = useBasket();
    const { data: products = [] } = useProducts(basket?.productItems?.map((pi) => pi.productId));

    const preferences = useCustomPreferences([
        CustomPreference.AFTERPAY_MPID,
        CustomPreference.AFTERPAY_PLACEMENT_ID,
    ]);

    const mpid = preferences[CustomPreference.AFTERPAY_MPID];
    const placementId = preferences[CustomPreference.AFTERPAY_PLACEMENT_ID];

    if (!afterpayConfig || !mpid || !placementId) {
        return null;
    }

    const amount = product?.price ?? basket?.orderTotal;
    const className = product
        ? styles.afterpay_messaging
        : `${styles.afterpay_messaging} ${styles.afterpay_messaging_cart}`;

    const skus = product?.id ?? basket?.productItems?.map((item) => item.productId).join(',');
    const categories =
        product?.c_primaryCategory?.name ??
        products.map((p) => p.c_primaryCategory?.name).join(',');

    const { minThreshold, maxThreshold } = afterpayConfig;
    const enableLoan = isBasketEligibleForMonthlyLoan(amount, afterpayConfig.payByMonthEnabled);

    return (
        <div data-testid="afterpay-messaging-container" className={className}>
            <square-placement
                data-mpid={mpid}
                data-placement-id={placementId}
                data-page-type={product ? 'product' : 'cart'}
                data-consumer-locale="en_US"
                data-item-skus={skus}
                data-item-categories={categories}
                data-is-eligible={eligible}
                data-testid="afterpay-messaging-widget"
                data-locale="en_US"
                data-intro-text="Or"
                data-currency="USD"
                data-cart-is-eligible={eligible}
                data-apr-loans-available={enableLoan ? 'true' : undefined}
                data-amount={amount}
                data-min={minThreshold}
                data-max={maxThreshold}
            />
        </div>
    );
};

AfterPayMessaging.defaultProps = {
    product: undefined,
};
